import React, { useContext } from 'react';
import { Card, Spin, Tooltip } from 'antd';
import { colorConstants } from 'config/colorConstants';
import { CloseOutlined, LoadingOutlined, PlusSquareOutlined } from '@ant-design/icons';

import {
    formatNumWithKMBAlt2,
    getConfig,
    formatNumWithKMBAlt,
    getAccessIcon,
    sendNotification,
} from 'utils/helperMethods';
import { ConfigKeys, PricingSimulatorStatusType } from 'utils/Enums';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import LabelWithPopover from './LabelWithPopover';
import styled from '@emotion/styled';
import { AT_LEAST_ONE_KPI_MUST_BE_SELECTED } from 'config/constants';

interface Attribute {
    label: string;
    id: number;
    absChange?: number;
    newValue?: number;
    oldValue?: number;
    percentChange?: number;
}

interface CardProps {
    name?: string;
    scenarioId: number | null;
    status?: PricingSimulatorStatusType;
    attributes?: Attribute[];
    isDeletable?: boolean;
    isParent?: boolean;
    onDelete?: () => void;
    selectedAttributeId?: number;
    onSelect?: (id: number) => void;
    isEmpty?: boolean;
    isLoading?: boolean;
    isPrivate?: boolean;
    showOldValue?: boolean;
}

export const CustomCard = styled(Card)`
    .ant-card-head-title {
        white-space: normal !important;
    }
    .ant-card-head-wrapper {
        align-items: start;
    }
`;

const KpiComparisonCard: React.FC<CardProps> = ({
    name,
    scenarioId,
    status,
    attributes,
    isDeletable,
    isParent,
    selectedAttributeId = 0,
    onDelete,
    onSelect,
    isEmpty,
    isLoading,
    isPrivate,
    showOldValue,
}) => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig(ConfigKeys?.PRICING_SIMULATOR, appConfig);
    const commonConfig = getConfig(ConfigKeys?.COMMON_CONFIG, appConfig);
    const { enablePrivatePublicToggle, defaultGlobalKpiCount } = pricingSimulatorConfig;

    const projectStatuses = pricingSimulatorConfig.projectStatuses;
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;
    const { setOpenScenarioPopup, isUpdate, updateReportProperty, selectedReportInfo } =
        useContext(PricingAnalyticsDataContext);

    // Row click handler
    const onRowClick = (attribute: Attribute) => {
        if (onSelect) {
            onSelect(attribute.id);
        }
    };
    const handleCloseClick = (event: React.MouseEvent, attributeId: Number) => {
        const selectedKpiIds = selectedReportInfo?.baseKPIs?.selectedKPIs;
        if (selectedKpiIds?.length === 1) {
            sendNotification('error', AT_LEAST_ONE_KPI_MUST_BE_SELECTED);
            return;
        }
        if (attributeId === selectedAttributeId) return;
        updateReportProperty('baseKPIs', {
            ...selectedReportInfo?.baseKPIs,
            selectedKPIs: selectedReportInfo?.baseKPIs.selectedKPIs.filter((item) => item !== attributeId) || [],
        });
        event.stopPropagation();
    };

    // Set row class based on selection status
    const rowClassName = (isSelected?: boolean) => (isSelected ? 'bg-bgBackground' : '');

    if (isEmpty) {
        // for read only user don't show placeholder component
        if (!isUpdate) {
            return <></>;
        }
        return (
            <div
                className="font-xxs flex flex-col gap-4 items-center text-backgroundGreyAlt2 rounded-xl justify-center border border-dashed border-borderGrey px-8 text-center text-promoColour cursor-pointer"
                style={{ minWidth: '248px', maxWidth: '248px' }}
                onClick={() => isUpdate && setOpenScenarioPopup(true)}
            >
                <PlusSquareOutlined style={{ fontSize: '20px' }} />
                <span> Add Scenario from “Select Scenarios” button or click here</span>
            </div>
        );
    }

    const formatValue = (value: any) => {
        return value ? formatNumWithKMBAlt2(value, decimalForNonPercentage, decimalForNonPercentage) : '-';
    };

    const oneKpiHeight = 41.42;
    const dynamicMaxHeight = defaultGlobalKpiCount * oneKpiHeight;

    return (
        <CustomCard
            title={
                <div className="flex justify-start  items-start">
                    {!isParent && (
                        <Tooltip
                            placement="top"
                            color="white"
                            destroyTooltipOnHide
                            overlayStyle={{ borderRadius: 4 }}
                            overlayInnerStyle={{
                                color: colorConstants.darkGrey,
                            }}
                            title={projectStatuses[status ?? 'DRAFT']?.label}
                        />
                    )}

                    {isParent ? (
                        <span className="text-black h-[75px]"> {name} </span>
                    ) : (
                        <>
                            {enablePrivatePublicToggle &&
                                getAccessIcon(isPrivate ?? false, { marginRight: 4, top: 3, marginLeft: 0 })}
                            <LabelWithPopover scenarioId={scenarioId || 0} name={name || ''} status={status} />
                        </>
                    )}
                </div>
            }
            extra={
                isDeletable && (
                    <span className="cursor-pointer" onClick={onDelete}>
                        <CloseOutlined />
                    </span>
                )
            }
            style={{
                borderRadius: '10px',
                border: '1px solid #e2e2e2',
                width: '252px',
            }}
            bodyStyle={{ padding: 0 }}
            headStyle={{ backgroundColor: colorConstants.greyAlt6, padding: '0px 12px' }}
        >
            <div
                className=" comparison-kpi-card  overflow-y-auto min-w-[250px]"
                style={{ maxHeight: `${dynamicMaxHeight}px` }}
            >
                {isLoading ? (
                    <div className="h-full flex justify-center items-center my-8">
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
                    </div>
                ) : (
                    <>
                        {attributes?.map((attribute) => {
                            const absValue = formatNumWithKMBAlt2(
                                attribute?.absChange || 0,
                                decimalForNonPercentage,
                                decimalForNonPercentage,
                            );

                            const percentChangeValue = `${
                                attribute?.percentChange?.toString() === 'NaN'
                                    ? '-'
                                    : `${formatNumWithKMBAlt(Number(attribute?.percentChange), decimalForPercentage)}%`
                            }`;

                            const isPositive = showOldValue
                                ? (attribute?.percentChange ?? 0) > 0
                                : (attribute?.absChange || 0) > 0;

                            const isSameValue = attribute?.oldValue === attribute?.newValue;

                            return (
                                <div
                                    key={attribute.id}
                                    className={`kpi-attribute-row max-w-[250px] ${rowClassName(
                                        attribute.id === selectedAttributeId,
                                    )}  p-2.5 cursor-pointer border-b  border-backgroundGreyAlt1 group`}
                                    onClick={() => onRowClick(attribute)}
                                >
                                    {isParent ? (
                                        <span className="flex justify-between items-center">
                                            <Tooltip
                                                placement="top"
                                                color="white"
                                                destroyTooltipOnHide
                                                overlayStyle={{ borderRadius: 4 }}
                                                overlayInnerStyle={{
                                                    color: colorConstants.darkGrey,
                                                }}
                                                title={attribute.label}
                                            >
                                                <div className="text-left font-xs text-backgroundGreyAlt2 truncate pr-2">
                                                    {attribute.label}
                                                </div>
                                            </Tooltip>
                                            {attribute.id !== selectedAttributeId && (
                                                <CloseOutlined
                                                    className={`${
                                                        attribute.id !== selectedAttributeId
                                                            ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                                                            : 'opacity-0'
                                                    } !text-xs`}
                                                    onClick={(event) => handleCloseClick(event, attribute.id)}
                                                />
                                            )}
                                        </span>
                                    ) : (
                                        <div className="flex w-full items-center">
                                            <div className="w-1/3 text-center font-xs ">
                                                {formatValue(showOldValue ? attribute.oldValue : attribute.newValue)}
                                            </div>

                                            <div className="w-1/3 text-center">
                                                <div
                                                    className="font-xs flex justify-center items-center gap-1"
                                                    style={{
                                                        color: isSameValue
                                                            ? colorConstants.greyAlt15
                                                            : isPositive
                                                            ? colorConstants.limeGreen
                                                            : colorConstants.red,
                                                    }}
                                                >
                                                    {absValue}
                                                    {/* {!isSameValue &&
                                                        (isPositive ? (
                                                            <ArrowUpOutlined style={{ fontSize: '15px' }} />
                                                        ) : (
                                                            <ArrowDownOutlined style={{ fontSize: '15px' }} />
                                                        ))} */}
                                                </div>
                                            </div>

                                            <div className="w-1/3 text-center">
                                                <div
                                                    className="font-xs flex justify-center items-center gap-1"
                                                    style={{
                                                        color: isSameValue
                                                            ? colorConstants.greyAlt15
                                                            : isPositive
                                                            ? colorConstants.limeGreen
                                                            : colorConstants.red,
                                                    }}
                                                >
                                                    {percentChangeValue}
                                                    {/* {!isSameValue &&
                                                        (isPositive ? (
                                                            <ArrowUpOutlined style={{ fontSize: '15px' }} />
                                                        ) : (
                                                            <ArrowDownOutlined style={{ fontSize: '15px' }} />
                                                        ))} */}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </CustomCard>
    );
};

export default KpiComparisonCard;
